import type { CityConfig } from "@/pages/city-template/types";

export const lenexaConfig: CityConfig = {
  city: "Lenexa",
  state: "Kansas",
  stateAbbr: "KS",
  slug: "lenexa",
  route: "/cities/lenexa",
  title: "Emergency Passport Lenexa, KS | Fast Passport Renewal & Expedited Services",
  metaDescription: "Emergency passport and expedited passport services in Lenexa, KS — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Lenexa and Johnson County travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Lenexa, Kansas",
  heroSubtitle: "Unexpected business trip to Europe and your passport expired? Flying out of MCI in days? Fast Passport Center helps Lenexa travelers get their passports fast — as fast as 24 hours. A+ BBB rated. No office visit required for most services.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Lenexa%20Kansas%20suburban%20cityscape%20with%20modern%20mixed-use%20development%20and%20tree-lined%20residential%20streets%2C%20Johnson%20County%20sunlit%20landscape%2C%20contemporary%20corporate%20campus%20architecture%20with%20prairie%20grass%20landscaping%2C%20Shawnee%20Mission%20Park%20greenery%20backdrop%2C%20warm%20golden%20hour%20light%2C%20Midwest%20suburban%20planning%20aesthetic%2C%20soft%20amber%20and%20sage%20green%20tones%2C%20photorealistic%20cityscape&width=1800&height=700&seq=lenexa-hero-01&orientation=landscape",
  heroImageAlt: "Lenexa Kansas modern suburban cityscape with corporate campuses and tree-lined neighborhoods",
  geoPosition: "38.9697;-94.7608",
  geoPlacename: "Lenexa, Kansas",
  lastModified: "2026-06-07",
  regionalAgency: {
    name: "Arkansas Passport Center",
    address: "191 Office Park Dr, Hot Springs, AR 71913",
    distance: "About 360 miles south — 5.5 hours by car",
  },
  nearbyCitiesZips: [
    { name: "Lenexa", zips: "66214–66227" },
    { name: "Overland Park", zips: "66204–66225" },
    { name: "Olathe", zips: "66051–66063" },
    { name: "Shawnee", zips: "66203–66226" },
    { name: "Leawood", zips: "66206–66211" },
    { name: "Prairie Village", zips: "66206–66208" },
    { name: "Merriam", zips: "66202–66204" },
    { name: "Mission", zips: "66201–66222" },
    { name: "Kansas City", zips: "66101–66160" },
    { name: "Gardner", zips: "66030–66031" },
    { name: "De Soto", zips: "66018–66019" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from home or work in Lenexa in minutes." },
    { icon: "ri-truck-line", title: "Ship Overnight from Johnson County", desc: "FedEx and UPS locations throughout Lenexa and the KC metro offer overnight shipping to our processing center." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed by our team before submission to reduce errors and delays — especially important for last-minute passport needs." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots at the nearest regional passport center — no public appointment hunting needed." },
  ],
  testimonials: [
    {
      name: "Mark T.",
      location: "Lenexa, KS",
      rating: 5,
      text: "Had a conference in Frankfurt with 4 days notice and realized my passport was expired. Fast Passport Center handled the emergency renewal — shipped from the FedEx on Lackman Road and had my passport back in 3 days. Made my flight from MCI with a day to spare. Incredible service.",
      service: "Emergency Passport",
    },
    {
      name: "Jennifer R.",
      location: "Lenexa, KS",
      rating: 5,
      text: "Needed passports for our whole family — two adults and three kids — for a spring break trip to Cancun. Fast Passport Center made the process so smooth, especially with the child passport requirements. Everything came back in 8 days. We'll be using them every time.",
      service: "Family Passports",
    },
    {
      name: "Tom H.",
      location: "Shawnee, KS",
      rating: 5,
      text: "My passport renewal was super straightforward through Fast Passport Center. Shipped my documents from the UPS on Renner Boulevard and had my new passport in 5 days. The document review gave me peace of mind — no mistakes, no delays.",
      service: "Passport Renewal",
    },
  ],
  facilities: [
    {
      id: "lenexa-main-post",
      name: "Lenexa Post Office",
      type: "USPS Passport Acceptance",
      address: "8820 Monrovia St",
      city: "Lenexa",
      state: "KS",
      zip: "66215",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Central Lenexa",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance location in Lenexa. Appointment required — book online well in advance. Photo services available on-site.",
      icon: "ri-mail-line",
    },
    {
      id: "overland-park-post",
      name: "Overland Park Main Post Office",
      type: "USPS Passport Acceptance",
      address: "12433 Antioch Rd",
      city: "Overland Park",
      state: "KS",
      zip: "66213",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Overland Park — about 5 miles east",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Nearby option for eastern Lenexa residents. Full-service passport acceptance with photo services.",
      icon: "ri-mail-line",
    },
    {
      id: "johnson-county-clerk-lenexa",
      name: "Johnson County Clerk's Office",
      type: "County Passport Acceptance",
      address: "111 S Cherry St, Suite 1500",
      city: "Olathe",
      state: "KS",
      zip: "66061",
      phone: "913-715-3300",
      hours: "Mon–Fri 8am–4:30pm (by appointment)",
      distance: "Olathe — about 10 miles south",
      website: "https://www.jocogov.org/",
      websiteLabel: "Johnson County Government",
      notes: "County clerk passport acceptance. Appointment recommended. May have shorter wait times than USPS locations.",
      icon: "ri-government-line",
    },
    {
      id: "shawnee-post",
      name: "Shawnee Post Office",
      type: "USPS Passport Acceptance",
      address: "10601 Shawnee Mission Pkwy",
      city: "Shawnee",
      state: "KS",
      zip: "66203",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Shawnee — about 6 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Convenient for northern Lenexa and Shawnee residents.",
      icon: "ri-mail-line",
    },
    {
      id: "merriam-post",
      name: "Shawnee Mission Post Office",
      type: "USPS Passport Acceptance",
      address: "6029 Broadmoor St",
      city: "Mission",
      state: "KS",
      zip: "66202",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Mission — about 8 miles northeast",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Alternative option for northeastern Johnson County residents.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport in Lenexa before international travel?",
      a: "Yes. Through Fast Passport Center, Lenexa travelers can get an expedited passport in as little as 2–9 business days, and emergency processing is available in as fast as 24 hours depending on your eligibility and travel documentation. Start your order online today.",
    },
    {
      q: "How fast can I renew my passport in Lenexa?",
      a: "Fast passport renewal in Lenexa is available through Fast Passport Center in as little as 3 business days with our expedited service. Renewal applications are eligible if your current passport was issued at age 16 or older and within the last 15 years. No office visit required for most renewals.",
    },
    {
      q: "Does Lenexa have its own passport agency?",
      a: "Lenexa, Kansas does not have a regional passport agency. The closest regional passport center is the Arkansas Passport Center in Hot Springs, Arkansas (about 360 miles south). Fast Passport Center is the faster and more convenient solution for Lenexa and Johnson County residents — no road trip required.",
    },
    {
      q: "What if my passport expires before my business trip from MCI?",
      a: "If your passport expires before your flight from Kansas City International (MCI), don't wait. Most countries require at least 3–6 months of passport validity beyond your stay. Fast Passport Center can expedite your passport renewal so you don't miss your business trip. The sooner you start, the more options you have.",
    },
    {
      q: "Can I expedite a child passport in Lenexa?",
      a: "Yes. Child passport expediting in Lenexa is possible through Fast Passport Center, but child passports for minors 15 and under require an in-person acceptance step with parental consent. We provide a clear checklist and instructions so you know exactly what to expect.",
    },
    {
      q: "How long does a standard passport take in Lenexa?",
      a: "If you apply through a standard post office acceptance facility in Lenexa, processing times run 6–13 weeks — sometimes longer during peak travel season. If your travel is sooner than that, you need expedited processing. Fast Passport Center offers Lenexa travelers processing as fast as 24 hours.",
    },
    {
      q: "What airports do Lenexa passport holders typically fly from?",
      a: "Kansas City International Airport (MCI) is the primary airport for Lenexa travelers, just 30 miles north with direct international flights. A valid passport is required for all international travel from MCI. Fast Passport Center can help you meet your deadline.",
    },
    {
      q: "Can I get a passport renewal near me in Lenexa without visiting an office?",
      a: "Yes. Most Lenexa adults who are renewing a passport can do so entirely by mail — no in-person visit required. Start your order online with Fast Passport Center, complete your documents at home, and ship them overnight from any FedEx or UPS location in Lenexa.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Lenexa", address: "15100 W 87th St Pkwy, Lenexa, KS 66219", hours: "Daily 7 AM – 10 PM" },
    { name: "CVS — Lenexa", address: "15100 W 87th St, Lenexa, KS 66219", hours: "Daily 7 AM – 10 PM" },
    { name: "FedEx Office — Lenexa", address: "12242 W 95th St, Lenexa, KS 66215", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 8 AM – 8 PM" },
    { name: "Walgreens — Overland Park", address: "11900 College Blvd, Overland Park, KS 66210", hours: "Daily 8 AM – 10 PM" },
    { name: "CVS — Shawnee", address: "11200 Shawnee Mission Pkwy, Shawnee, KS 66203", hours: "Daily 7 AM – 10 PM" },
  ],
  dmvLocations: [
    {
      name: "Olathe Driver License Office",
      address: "13500 S Mur-Len Rd, Suite B, Olathe, KS 66062",
      hours: "Mon–Fri 7 AM – 5:45 PM",
      note: "Johnson County DMV office. Driver licenses, REAL ID, ID cards. Appointment recommended.",
      featured: true,
    },
    {
      name: "Mission Driver License Office",
      address: "6507 Johnson Dr, Mission, KS 66202",
      hours: "Mon–Fri 7 AM – 5:45 PM",
      note: "Alternative Johnson County DMV for northern Johnson County residents.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "MCI",
      name: "Kansas City International Airport",
      distance: "Primary international airport — 30 miles north",
      address: "1 International Sq, Kansas City, MO 64153",
      note: "MCI is the closest major airport for Lenexa travelers. Direct international flights available. Busiest travel seasons are May–August and November–January — plan passport needs well in advance.",
      flightUrl: "https://www.flykci.com/",
      flightLabel: "MCI Airport Website",
      featured: true,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Flying from MCI in 24–72 hours? Our emergency courier service is designed for urgent, last-minute travel from Lenexa and the Kansas City metro.",
      bullets: ["As fast as 24 hours", "Reserved agency submission slots", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already have a U.S. passport? We help Lenexa and Johnson County travelers renew fast — passport expired before trip is one of our most common urgent requests.",
      bullets: ["Eligible for expedited processing", "Ship from any FedEx or UPS in Lenexa", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport before a trip? We guide Lenexa travelers through Form DS-64 and the replacement process — urgent timelines available.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Child passport expedited service for Lenexa families — minors under 16 require in-person acceptance and parental consent. We provide clear instructions and checklists.",
      bullets: ["Both parents must typically consent", "Clear checklist provided", "Expedited options after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Lenexa residents — we guide you through the acceptance clerk process and handle expedited processing.",
      bullets: ["Step-by-step checklist", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Passport Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — we submit through reserved agency channels, not public lines.",
      bullets: ["State Dept. registered courier", "Reserved daily submission slots", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Other Services",
      description: "Passport name changes after marriage or divorce, damaged passport replacement, second passports for frequent travelers — all handled with the same expedited courier access.",
      bullets: ["Name change after marriage/divorce", "Damaged passport replacement", "Second passport for frequent travel"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Lenexa, Kansas travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Expedited Passport", description: "Expedited passport processing for Lenexa residents needing passports faster than standard government mail timelines." },
    { name: "Passport Renewal", description: "Fast passport renewal for Lenexa, KS residents with a passport issued within the last 15 years at age 16 or older." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement service for Lenexa travelers with lost or stolen passports." },
    { name: "Child Passport", description: "Expedited child passport services for Lenexa families with minors under 16." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Too Long to Apply", description: "Lenexa travelers often assume 6–8 weeks is safe — it's not. Government processing has stretched to 10–14+ weeks during peak travel season. Apply as soon as you book your trip." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photos from drugstores and photo booths often get rejected for lighting, background, or expression issues. We provide exact specs and can catch these issues before documents are submitted." },
    { icon: "ri-file-damage-line", title: "Incomplete or Incorrect Forms", description: "One wrong date, one missing signature — your application gets sent back and you lose weeks. Our checklist catches errors before submission." },
    { icon: "ri-calendar-close-line", title: "Assuming the Post Office is Fast Enough", description: "Standard USPS passport processing out of Lenexa runs 6–13 weeks. If your trip is in under 10 weeks, standard processing is a gamble. Use our courier service instead." },
    { icon: "ri-map-pin-time-line", title: "Name Mismatch Across Documents", description: "Your passport name must match your airline ticket exactly. Middle names, hyphens, and suffixes cause more rejections than you'd expect." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your Lenexa travel date. Complete your secure order online — takes just a few minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We confirm your details and send you a personalized document checklist — exactly what you need, nothing more." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight from Lenexa", description: "Drop off at any FedEx or UPS in the Lenexa or KC metro — most locations are open evenings and weekends." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit your application through our reserved passport agency slots — bypassing the public appointment system entirely." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport is Completed", description: "Your passport is completed within the timeframe you selected and shipped back to you." },
  ],
  processingOptions: [
    {
      speed: "24", unit: "Hours*", label: "Regional Agency Processing",
      tag: "Emergency Only", tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required", "Life-or-death emergencies only", "Expect to wait all day or return next day"],
      highlight: false, emergency: true,
    },
    {
      speed: "2–9", unit: "Business Days", label: "Fast Passport Center",
      tag: "Most Popular", tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Document review included", "Dedicated agent assigned", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5", unit: "Weeks", label: "Fast Passport Center",
      tag: "Standard Expedited", tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Document review included"],
      highlight: false, smartSaver: true,
    },
    {
      speed: "1–2", unit: "Months", label: "Self-Service (Post Office)",
      tag: "Slowest", tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access", "In-person clerk appointment required", "No document review", "No dedicated agent", "Not suitable if travel is within 10 weeks"],
      highlight: false, muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited slots at Arkansas Passport Center", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Must drive to Hot Springs, AR (5.5 hours)", fpc: "Ship documents overnight from Lenexa" },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission slots" },
    { feature: "Government Shutdowns", agency: "Operations may be impacted", fpc: "Not affected by government shutdowns" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Overland Park", route: "/cities/overland-park" },
    { name: "Fast passport renewal in Olathe", route: "/cities/olathe" },
    { name: "Emergency passport help near Shawnee", route: "/cities/shawnee-ks" },
    { name: "Rush passport service in Leawood", route: "/cities/leawood" },
    { name: "Fast passport renewal for Johnson County", route: "/states/kansas" },
  ],
  fpcOfficeNote: "After signing up online, we send your personalized checklist and shipping instructions. Ship your documents overnight from any FedEx or UPS location in Lenexa or the Kansas City metro area — most are open evenings. No office visit to our facility required for most services.",
  fpcOfficeDistance: "Serving Lenexa and all of Johnson County remotely",
  serviceAreaIntro: "Whether you are in Lenexa, Overland Park, Olathe, Shawnee, or anywhere in Johnson County, Fast Passport Center makes expedited passport processing simple — start online, ship overnight, and we handle the rest.",
  nearbyCitiesIntro: "We serve all Lenexa and Johnson County zip codes. Anywhere in the Kansas City metro — ship overnight and your documents are at our processing center the next morning.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Lenexa as simple as possible. Most Lenexa clients never visit an office.",
  mistakesIntro: "These mistakes cause the most delays and rejections for Lenexa travelers. Fast Passport Center reviews your documents before submission to catch these issues.",
  testimonialsIntro: "Real reviews from Lenexa and Johnson County travelers who needed their passports fast and trusted Fast Passport Center.",
  faqIntro: "The most common questions Lenexa and Johnson County travelers ask about emergency and expedited passport services.",
  vitalRecordsIcon: "ri-heart-pulse-line",
  vitalRecordsLinks: [
    { label: "Birth & Death Certificates (Kansas Vital Statistics)", url: "https://www.kdhe.ks.gov/359/Vital-Statistics", desc: "Kansas Department of Health & Environment — Vital Statistics for birth and death certificates statewide" },
    { label: "Marriage Licenses (Johnson County Clerk)", url: "https://www.jocogov.org/", desc: "Johnson County Clerk for Lenexa and Johnson County" },
    { label: "Divorce Decrees (Johnson County District Court)", url: "https://www.jocogov.org/dept/district-court/", desc: "Johnson County District Court for divorce decree records" },
    { label: "VitalChek Online Ordering", url: "https://www.vitalchek.com/", desc: "Order Kansas vital records online via VitalChek" },
  ],
  statePageRoute: "/states/kansas",
  statePageName: "Kansas",
};